/** Letter or digit (accents included). Kept outside `[]` so the `u` flag is enough. */
const LNUM = String.raw`(?:\p{L}|\p{N})`;
const UPPER_WORD = String.raw`(?:\p{Lu}|\p{N})(?:${LNUM}|'|-)*`;
const LOWER_WORD = String.raw`\p{Ll}(?:${LNUM}|'|-)*`;
const PLACEHOLDER = String.raw`\[[^\]]{1,60}\]`;
/** French (and similar) particles between menu words: "Calcul de salaire". */
const PARTICLE = String.raw`(?:de|des|du|d'|la|le|les|et|à|l')`;
const SEP = String.raw`[ /&'-]+`;
const REST = `(?:${PLACEHOLDER}|${UPPER_WORD}|(?:${PARTICLE} )+(?:${UPPER_WORD}|${LOWER_WORD}))`;
const LABEL = `(?:${PLACEHOLDER}|${UPPER_WORD}(?:${SEP}${REST})*)`;
/** Optional markdown emphasis/code around a segment (`**`, `*`, `__`, `_`, `` ` ``). */
const MARK = String.raw`(?:\*{1,2}|_{1,2}|` + '`)?';
const WRAPPED_SEGMENT = `${MARK}${LABEL}${MARK}`;
/** Sentence verbs that often precede a path ("Open Employees → New") and are not menu labels. */
const LEAD_VERB = String.raw`(?:Open|Go|See|Use|Click|Try|Select|Choose|From|Via|Follow|Check|Visit|Aller|Ouvrir|Utilisez|Cliquez)\s+`;
const MENU_PATH_RE = new RegExp(
  `(?<!${LNUM})(${LEAD_VERB})?${WRAPPED_SEGMENT}(?:\\s*→\\s*${WRAPPED_SEGMENT})+(?!${LNUM})`,
  'gu'
);

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function stripMdWrappers(segment: string): string {
  return segment.replace(/^[`*_]+|[`*_]+$/g, '').trim();
}

function isPlaceholder(segment: string): boolean {
  return segment.startsWith('[') && segment.endsWith(']') && segment.length > 2;
}

function wrapPath(matched: string): string {
  const segments = matched
    .split(/\s*→\s*/)
    .map(stripMdWrappers)
    .filter(Boolean);
  if (segments.length < 2) return matched;

  // Real menu roots are labels like "Employees", not "5" or "HR".
  const firstPlain = isPlaceholder(segments[0]!) ? segments[0]!.slice(1, -1) : segments[0]!;
  if (firstPlain.length < 3 || /^\d+$/.test(firstPlain)) {
    return matched;
  }

  const aria = segments.map((s) => (isPlaceholder(s) ? s.slice(1, -1) : s)).join(', ');
  const chips = segments
    .map((seg, i) => {
      const cls = isPlaceholder(seg) ? 'menu-path-seg menu-path-seg--placeholder' : 'menu-path-seg';
      const chip = `<kbd class="${cls}">${escapeHtml(seg)}</kbd>`;
      if (i === 0) return chip;
      return `<span class="menu-path-sep" aria-hidden="true">→</span>${chip}`;
    })
    .join('');

  return `<span class="menu-path" role="group" aria-label="${escapeHtml(aria)}">${chips}</span>`;
}

function replacePathsInText(text: string): string {
  if (!text.includes('→')) return text;
  // Never rewrite arrows inside KaTeX regions.
  const slots: string[] = [];
  const protectedText = text
    .replace(/\$\$([\s\S]+?)\$\$/g, (_full, body: string) => {
      const i = slots.length;
      slots.push(`$$${body}$$`);
      return `\0MATH${i}\0`;
    })
    .replace(/(?<!\$)\$(?!\$)([\s\S]+?)(?<!\$)\$(?!\$)/g, (_full, body: string) => {
      const i = slots.length;
      slots.push(`$${body}$`);
      return `\0MATH${i}\0`;
    });

  const replaced = protectedText.replace(MENU_PATH_RE, (full, verbPrefix: string | undefined) => {
    const path = verbPrefix ? full.slice(verbPrefix.length) : full;
    return `${verbPrefix ?? ''}${wrapPath(path)}`;
  });

  return replaced.replace(/\0MATH(\d+)\0/g, (_m, idx: string) => slots[Number(idx)] ?? '');
}

/**
 * Turns Payroll App menu paths (`Employees → [employee] → Salary Settings`)
 * into breadcrumb chips. Skips fenced code so charts and snippets stay intact.
 */
export function highlightMenuPaths(markdown: string): string {
  if (!markdown.includes('→')) return markdown;

  let result = '';
  let lastIndex = 0;
  const fenceRe = /```[\s\S]*?(?:```|$)/g;
  let match: RegExpExecArray | null;
  while ((match = fenceRe.exec(markdown)) !== null) {
    result += replacePathsInText(markdown.slice(lastIndex, match.index));
    result += match[0];
    lastIndex = match.index + match[0].length;
  }
  result += replacePathsInText(markdown.slice(lastIndex));
  return result;
}
